<template lang="pug">
.devices
  //- Barra de navegación
  van-nav-bar(
    :title="$t('devices.selectZone')"
    left-arrow
    @click-left="close")

  //- Sección principal
  .main(v-if="installations")

    //- Listado de instalaciones
    .div(
      v-for="(installation, installationKey) in installations"
      :key="installationKey")

      //- Nombre de la instalación
      .van-cell-group__title {{ installation.name }}

      //- Listado de dispositivos
      div(
        v-for="(unit, mac) in installation.devices"
        :key="mac")
        .units__device__bar(v-if="checkIsSelected(installationKey, mac)")
          .units__device__name
            van-icon.media__figure(:name="GetDeviceIcon(unit)")
            | {{ unit.name}}
          .units__device__action
            van-icon(name="save")
        .units__device__bar(v-else-if="unit.link_deviceID || !unit.pro")
          .units__device__name.disabled
            van-icon.media__figure(:name="GetDeviceIcon(unit)")
            span
              | {{ unit.name}}
              br
              span(v-if="unit.link_deviceID") {{ $t('devices.deviceLinked') }}
              span(v-else) {{ $t('devices.notPro') }}
        .units__device__bar(
          v-else
          @click="chooseFallback(installationKey, mac)")
          .units__device__name
            van-icon.media__figure(:name="GetDeviceIcon(unit)")
            | {{ unit.name}}

  //- Botón de desvincular un dispostivo
  .button-bottom-group(v-if="device.unit")
    button.button-bottom.text-danger(@click="confirmUnlinkDevice") {{ $t('devices.unlinkDevice') }}
</template>

<script>
import DevicesService from '@/services/devices.service';
import GetDeviceIcon from '@/mixins/GetDeviceIcon.mixin';
import { getErrorId } from '@/services/error.service';
import { mapGetters } from 'vuex';

export default {
  mixins: [GetDeviceIcon],

  data() {
    return {
      isLoading: false,
    };
  },

  props: {
    device: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      getAdvancedInstallations: 'unitsModule/getAdvancedInstallations',
    }),

    /**
     * Listado con todas las instalaciones
     *
     * @return {Array[Installation]} - Listado de instalaciones
     */
    installations() {
      return this.getAdvancedInstallations();
    },
  },

  methods: {
    checkIsSelected(installationID, unitMac) {
      return installationID === this.device.installationID && unitMac === this.device.deviceMac;
    },

    chooseFallback(installationID, unitMac) {
      this.$dialog
        .confirm({
          title: this.$i18n.t('devices.fallbackTitle'),
          message: this.$i18n.t('devices.fallbackQuestion'),
          cancelButtonText: this.$i18n.t('devices.fallbackFalse'),
          confirmButtonText: this.$i18n.t('devices.fallbackTrue'),
          className: 'devicesFallback',
        })
        .then(() => {
          this.linkDevice(installationID, unitMac, true);
        })
        .catch(() => {
          this.linkDevice(installationID, unitMac, false);
        });
    },

    async linkDevice(installationID, unitMac, fallback) {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: this.$i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;

        const brand = this.device.brand;

        // Si ya está vinculado, primero tengo que desvincularlo
        if (this.device.deviceMac) {
          await DevicesService.unlinkDevice(
            brand,
            this.device.installationID,
            this.device.deviceMac
          );
        }

        const data = {};

        data.fallback = fallback;

        data.ws = {
          device_id: unitMac,
          installation_id: installationID,
        };

        if (brand === 'honeywell') {
          data.th = {
            macID: this.device.mac,
            locationID: this.device.location,
            deviceID: this.device.id,
          };
        } else if (brand === 'ecobee') {
          data.th = {
            ecob_id: this.device.id,
          };
        } else if (brand === 'nest') {
          data.th = {
            ext_device_id: this.device.id,
          };
        }

        // Confirmo los cambios
        this.$dialog
          .alert({
            title: this.$i18n.t('devices.linkDeviceSuccess.title'),
            message: this.$i18n.t('devices.linkDeviceSuccess.message'),
          })
          .then(() => {
            this.close(true);
          });

        // Realizo la petición
        await DevicesService.linkDevice(brand, data);
        this.$toast.clear();

        // Confirmo los cambios
        this.$dialog
          .alert({
            title: this.$i18n.t('devices.linkDeviceSuccess.title'),
            message: this.$i18n.t('devices.linkDeviceSuccess.message'),
          })
          .then(() => {
            this.close(true);
          });
      } catch (error) {
        // Obtengo el error
        const linkError = getErrorId(error);

        this.$toast.clear();

        if (linkError === 'deviceNotConnected') {
          this.$dialog
            .alert({
              title: this.$i18n.t('devices.linkError.deviceNotConnected.title'),
              message: this.$i18n.t('devices.linkError.deviceNotConnected.message'),
            })
            .then(() => {
              this.close(true);
            });
        } else {
          this.$dialog
            .alert({
              title: this.$i18n.t('errors.default.title'),
              message: this.$i18n.t('errors.default.message'),
            })
            .then(() => {
              this.close(true);
            });
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Confirma si realmente quiere desvincular el dispositivo
     */
    confirmUnlinkDevice() {
      this.$dialog
        .confirm({
          title: this.$i18n.t('devices.unlinkDeviceConfirm.title'),
          message: this.$i18n.t('devices.unlinkDeviceConfirm.message'),
        })
        .then(() => {
          this.unlinkDevice();
        });
    },

    /**
     * Desvincula el dispositivo
     */
    async unlinkDevice() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: this.$i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;

        // Realizo la petición
        await DevicesService.unlinkDevice(
          this.device.brand,
          this.device.installationID,
          this.device.deviceMac
        );
        this.$toast.clear();

        // Confirmo los cambios
        this.$dialog
          .alert({
            title: this.$i18n.t('devices.unlinkDeviceSuccess.title'),
            message: this.$i18n.t('devices.unlinkDeviceSuccess.message'),
          })
          .then(() => {
            this.close(true);
          });
      } catch (error) {
        // Obtengo el error
        getErrorId(error);

        this.$toast.clear();

        this.$dialog
          .alert({
            title: this.$i18n.t('errors.default.title'),
            message: this.$i18n.t('errors.default.message'),
          })
          .then(() => {
            this.close();
          });
      } finally {
        this.isLoading = false;
      }
    },

    close(reload) {
      this.$emit('close', reload);
    },
  },
};
</script>
